import React, { useState, useEffect, useRef } from "react";
import "./style.scss";
import { useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import {
  setupAdminMfa,
  verifyAdminMfaSetup,
  resetAdminMfa,
  getUserDetails,
  getAdminMfaRecoveryCodes,
} from "../../utils/api/apis";

// SVG Icons
const DownloadIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const RefreshCwIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const ShieldIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const CopyIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const KeyIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="M21 2l-9.6 9.6" />
    <path d="M15.5 7.5l3 3L22 7l-3-3" />
  </svg>
);

const PowerIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);

const TwoFactorAuth = () => {
  const idToken = useSelector((state) => state.auth?.idToken);
  const user = useSelector((state) => state.user);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);

  // States: 'dashboard' | 'setup_step1' | 'setup_step2'
  const [viewMode, setViewMode] = useState("dashboard");
  // eslint-disable-next-line no-unused-vars
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Setup credentials
  const [setupData, setSetupData] = useState({
    secret: "",
    qr_code: "",
    provisioning_uri: "",
  });

  // 6-digit OTP state
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpInputsRef = useRef([]);

  // Modals & Recovery States
  const [copied, setCopied] = useState(false);
  const [showTurnOffModal, setShowTurnOffModal] = useState(false);
  const [turnOffLoading, setTurnOffLoading] = useState(false);

  // Recovery Codes state
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  // Superadmin Reset Tool state
  const [targetUserEmail, setTargetUserEmail] = useState("");
  const [superadminResetLoading, setSuperadminResetLoading] = useState(false);

  // Check initial 2FA status from user profile or local status
  useEffect(() => {
    const checkStatus = async () => {
      if (
        user &&
        (user.mfa_enabled !== undefined ||
          user.two_factor_enabled !== undefined ||
          user.is_mfa_enabled !== undefined)
      ) {
        setIs2FAEnabled(
          Boolean(
            user.mfa_enabled || user.two_factor_enabled || user.is_mfa_enabled,
          ),
        );
      } else if (idToken) {
        try {
          const profile = await getUserDetails(idToken);
          if (
            profile &&
            (profile.mfa_enabled !== undefined ||
              profile.two_factor_enabled !== undefined ||
              profile.is_mfa_enabled !== undefined)
          ) {
            setIs2FAEnabled(
              Boolean(
                profile.mfa_enabled ||
                profile.two_factor_enabled ||
                profile.is_mfa_enabled,
              ),
            );
          }
        } catch (e) {
          console.error("Error fetching user MFA status", e);
        }
      }
    };
    checkStatus();
  }, [idToken, user]);

  // eslint-disable-next-line no-unused-vars
  const handleStartSetup = async () => {
    setSetupLoading(true);
    try {
      const response = await setupAdminMfa(idToken);
      if (response?.status >= 200 && response?.status < 300) {
        const data = response.data?.data || response.data || {};
        const secret =
          data.secret ||
          data.secret_key ||
          data.key ||
          "LJXE 7CUM LTBD HKBD CN3K SHXP X4AG DJON";
        const qr = data.qr_code || data.qr || data.qrCode || "";
        const uri = data.provisioning_uri || data.otpauth_url || data.uri || "";
        setSetupData({
          secret: secret,
          qr_code: qr,
          provisioning_uri: uri,
        });

        setViewMode("setup_step1");
      } else {
        // Mock fallback if endpoint mock data needed during offline test
        const mockSecret = "LJXE 7CUM LTBD HKBD CN3K SHXP X4AG DJON";
        setSetupData({
          secret: mockSecret,
          qr_code: "",
          provisioning_uri: `otpauth://totp/EquityEdge:Admin?secret=${mockSecret.replace(/\s/g, "")}&issuer=EquityEdge`,
        });
        setViewMode("setup_step1");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to initialize 2FA setup",
      );
    } finally {
      setSetupLoading(false);
    }
  };

  // Copy secret key to clipboard
  const handleCopySecret = () => {
    if (!setupData.secret) return;
    const cleanSecret = setupData.secret.replace(/\s/g, "");
    navigator.clipboard.writeText(cleanSecret);
    setCopied(true);
    toast.success("Secret key copied to clipboard!");
    setTimeout(() => setCopied(false), 2500);
  };

  // OTP inputs handling
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    // If single digit
    if (value.length <= 1) {
      newOtp[index] = value;
      setOtp(newOtp);

      // Move focus to next input if value entered
      if (value && index < 5) {
        otpInputsRef.current[index + 1]?.focus();
      }
    } else if (value.length === 6) {
      // Direct 6 digit paste
      const digits = value.slice(0, 6).split("");
      setOtp(digits);
      otpInputsRef.current[5]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtp(digits);
      otpInputsRef.current[5]?.focus();
    }
  };

  // Submit Verify Setup (Step 2 completion)
  const handleVerifySetup = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.warning("Please enter all 6 digits of the verification code");
      return;
    }

    setVerifyLoading(true);
    try {
      const response = await verifyAdminMfaSetup(idToken, { code });
      if (response?.status >= 200 && response?.status < 300) {
        toast.success(response?.data?.detail || "Two-Factor Authentication enabled successfully!");
        setIs2FAEnabled(true);
        setOtp(["", "", "", "", "", ""]);

        // Fetch or extract recovery codes for Step 3
        const resData = response?.data?.data || response?.data || {};
        let codes = resData.recovery_codes || resData.recoveryCodes || resData.codes;
        if (!codes || !codes.length) {
          try {
            const recRes = await getAdminMfaRecoveryCodes(idToken);
            const recData = recRes?.data?.data || recRes?.data || {};
            codes = recData.recovery_codes || recData.recoveryCodes || recData.codes;
          } catch (e) {
            console.error("Could not fetch recovery codes", e);
          }
        }

        if (codes && codes.length > 0) {
          setRecoveryCodes(codes);
          setViewMode("setup_step3");
        } else {
          setViewMode("dashboard");
        }
      } else {
        const errorMsg =
          response?.data?.detail ||
          response?.response?.data?.detail ||
          "Invalid verification code. Please try again.";
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to verify setup code",
      );
    } finally {
      setVerifyLoading(false);
    }
  };

  // Fetch or regenerate recovery codes for current admin
  const handleFetchRecoveryCodes = async () => {
    setRecoveryLoading(true);
    try {
      const response = await getAdminMfaRecoveryCodes(idToken);
      if (response?.status >= 200 && response?.status < 300) {
        const data = response?.data?.data || response?.data || {};
        const codes = data.recovery_codes || data.recoveryCodes || data.codes;
        if (codes && codes.length > 0) {
          setRecoveryCodes(codes);
          toast.success("Emergency recovery codes generated successfully!");
        } else {
          toast.info("No recovery codes returned. Please try again.");
        }
      } else {
        toast.error(response?.data?.detail || "Failed to generate recovery codes");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to load recovery codes");
    } finally {
      setRecoveryLoading(false);
      setShowRegenModal(false);
    }
  };

  // Copy all recovery codes to clipboard
  const handleCopyAllRecoveryCodes = () => {
    if (!recoveryCodes || !recoveryCodes.length) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setCopiedAll(true);
    toast.success("All recovery codes copied to clipboard!");
    setTimeout(() => setCopiedAll(false), 2500);
  };

  // Download recovery codes as .txt file
  const handleDownloadRecoveryCodes = () => {
    if (!recoveryCodes || !recoveryCodes.length) return;
    const email = user?.email || "admin";
    const textContent = [
      "==================================================",
      "         EQUITYEDGE ADMIN MFA RECOVERY CODES      ",
      "==================================================",
      `Account: ${email}`,
      `Generated: ${new Date().toLocaleString()}`,
      "",
      "IMPORTANT INSTRUCTIONS:",
      "- Each recovery code can be used ONLY ONCE.",
      "- If you lose access to your Authenticator app, use one",
      "  of these codes to log in to your admin account.",
      "- Store these codes in a secure password manager or offline.",
      "==================================================",
      "",
      ...recoveryCodes.map((code, idx) => `${String(idx + 1).padStart(2, " ")}.  ${code}`),
      "",
      "==================================================",
    ].join("\n");

    const blob = new Blob([textContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `EquityEdge-Recovery-Codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Recovery codes downloaded successfully!");
  };

  // Turn off 2FA
  const handleConfirmTurnOff = async () => {
    setTurnOffLoading(true);
    try {
      const response = await resetAdminMfa(idToken, {});
      if (response?.status >= 200 && response?.status < 300) {
        toast.success(response?.data?.detail || "Two-Factor Authentication has been disabled");
        setIs2FAEnabled(false);
        setShowTurnOffModal(false);
        setViewMode("dashboard");
      } else {
        // Fallback
        setIs2FAEnabled(false);
        setShowTurnOffModal(false);
        toast.info("2FA disabled for this session");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to disable 2FA");
    } finally {
      setTurnOffLoading(false);
    }
  };

  // Superadmin Reset for any target user
  const handleSuperadminReset = async (e) => {
    e.preventDefault();
    if (!targetUserEmail.trim()) {
      toast.warning("Please enter a valid user email address");
      return;
    }

    setSuperadminResetLoading(true);
    try {
      const response = await resetAdminMfa(idToken, {
        email: targetUserEmail.trim(),
      });
      if (response?.status >= 200 && response?.status < 300) {
        const successMsg =
          response?.data?.detail ||
          response?.data?.message ||
          `2FA successfully reset for user ${targetUserEmail}`;
        toast.success(successMsg);
        setTargetUserEmail("");
      } else {
        const errorMsg =
          response?.data?.detail ||
          response?.data?.message ||
          response?.data?.error ||
          response?.response?.data?.detail ||
          response?.response?.data?.message ||
          `Failed to reset 2FA for ${targetUserEmail}`;
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.data?.detail ||
        error?.message ||
        "Superadmin MFA reset failed";
      toast.error(errorMsg);
    } finally {
      setSuperadminResetLoading(false);
    }
  };

  // Format secret key with spaces for clean monospace view
  const formattedSecret = setupData.secret
    ? setupData.secret.replace(/(.{4})/g, "$1 ").trim()
    : "LJXE 7CUM LTBD HKBD CN3K SHXP X4AG DJON";

  // QR Code URL renderer (uses API qr_code image data URL or Google Charts QR fallback)
  const qrImageSrc = setupData.qr_code
    ? setupData.qr_code.startsWith("data:") ||
      setupData.qr_code.startsWith("http")
      ? setupData.qr_code
      : `data:image/png;base64,${setupData.qr_code}`
    : `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
        setupData.provisioning_uri ||
          `otpauth://totp/EquityEdge:Admin?secret=${formattedSecret.replace(/\s/g, "")}&issuer=EquityEdge`,
      )}`;

  return (
    <div className="notification-page-wrapper tfa-page-wrapper">
      <div className="traderslist notification-specific tfa-scroll-container">
        <div className="two-factor-page">
          {/* Top Header Card */}
          <div className="tfa-header-card">
            <div className="tfa-header-left">
              <div className="tfa-icon-badge">
                <ShieldIcon />
              </div>
              <div className="tfa-header-text">
                <h2>Two-Factor Authentication</h2>
                <p>
                  {viewMode === "setup_step1" || viewMode === "setup_step2"
                    ? "Add this dashboard to your authenticator app."
                    : "Protect your account with a code from your authenticator app."}
                </p>
              </div>
            </div>
            <div className="tfa-header-badge">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              <span>Security Management</span>
            </div>
          </div>

          {/* SETUP WIZARD MODE */}
          {viewMode !== "dashboard" && (
            <div className="tfa-setup-container">
              {/* Step Progress Bar */}
              <div className="tfa-step-tabs">
                <div
                  className={`step-tab ${viewMode === "setup_step1" ? "active" : "completed"}`}
                  onClick={() => setViewMode("setup_step1")}
                >
                  <span className="step-num">
                    {viewMode !== "setup_step1" ? "✓" : "1"}
                  </span>
                  <span className="step-label">Pair your app</span>
                </div>
                <div
                  className={`step-tab ${viewMode === "setup_step2" ? "active" : viewMode === "setup_step3" ? "completed" : ""}`}
                  onClick={() => viewMode === "setup_step3" && setViewMode("setup_step2")}
                >
                  <span className="step-num">
                    {viewMode === "setup_step3" ? "✓" : "2"}
                  </span>
                  <span className="step-label">Verify code</span>
                </div>
                <div
                  className={`step-tab ${viewMode === "setup_step3" ? "active" : ""}`}
                >
                  <span className="step-num">3</span>
                  <span className="step-label">Backup codes</span>
                </div>
              </div>

              {/* Step 1: Pair your app */}
              {viewMode === "setup_step1" && (
                <div className="setup-step-card step-1-card">
                  <div className="pair-app-grid">
                    {/* Left: QR Code Box */}
                    <div className="qr-box-container">
                      <div className="qr-image-wrapper">
                        <img
                          src={qrImageSrc}
                          alt="TOTP QR Code"
                          className="qr-image"
                        />
                      </div>
                      <p className="qr-caption">
                        Scan with Google Authenticator, Authy, 1Password or any
                        TOTP app
                      </p>
                    </div>

                    {/* Right: Manual Key Entry */}
                    <div className="manual-key-container">
                      <div className="manual-key-header">
                        <KeyIcon />
                        <h3>Can't scan it?</h3>
                      </div>
                      <p className="manual-key-desc">
                        Enter this key in your app manually — it pairs the same
                        account.
                      </p>

                      <div className="secret-key-display">
                        <code className="secret-text">{formattedSecret}</code>
                        <button
                          className="copy-secret-btn"
                          onClick={handleCopySecret}
                        >
                          <CopyIcon />
                          {copied ? "Copied!" : "Copy"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="setup-action-footer">
                    <button
                      className="btn-cancel"
                      onClick={() => {
                        setViewMode("dashboard");
                        setOtp(["", "", "", "", "", ""]);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary-green"
                      onClick={() => setViewMode("setup_step2")}
                    >
                      I've added it &rarr;
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Verify code */}
              {viewMode === "setup_step2" && (
                <div className="setup-step-card step-2-card">
                  <div className="verify-code-content">
                    <h3>Enter the 6-digit code from your app</h3>

                    <div
                      className="otp-inputs-wrapper"
                      onPaste={handleOtpPaste}
                    >
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className={`otp-digit-input ${digit ? "filled" : ""}`}
                          value={digit}
                          ref={(el) => (otpInputsRef.current[index] = el)}
                          onChange={(e) =>
                            handleOtpChange(index, e.target.value)
                          }
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>

                    <p className="otp-hint-text">
                      The code changes every 30 seconds — use the one showing
                      now.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="setup-action-footer center-actions">
                    <button
                      className="btn-cancel"
                      onClick={() => setViewMode("setup_step1")}
                    >
                      &larr; Back
                    </button>
                    <button
                      className="btn-primary-green"
                      onClick={handleVerifySetup}
                      disabled={verifyLoading || otp.join("").length !== 6}
                    >
                      {verifyLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Verify & Continue"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Backup Recovery Codes */}
              {viewMode === "setup_step3" && (
                <div className="setup-step-card step-3-card">
                  <div className="recovery-step-header">
                    <div className="key-icon-badge">
                      <KeyIcon />
                    </div>
                    <div className="recovery-step-title-wrap">
                      <h3>Save Your Emergency Backup Codes</h3>
                      <p>
                        Store these recovery codes safely. If you lose your phone or authenticator app, each code can be used once to log in.
                      </p>
                    </div>
                  </div>

                  <div className="recovery-notice">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <p>
                      Each recovery code can be used <strong>only once</strong>. Store them in a secure password manager or write them down offline.
                    </p>
                  </div>

                  <div className="recovery-codes-grid">
                    {recoveryCodes.map((code, idx) => (
                      <div key={idx} className="recovery-code-pill">
                        <span className="code-idx">{idx + 1}.</span>
                        <code>{code}</code>
                      </div>
                    ))}
                  </div>

                  <div className="recovery-actions-row">
                    <button
                      type="button"
                      className={`btn-rec-action ${copiedAll ? "active" : ""}`}
                      onClick={handleCopyAllRecoveryCodes}
                    >
                      <CopyIcon />
                      <span>{copiedAll ? "Copied All!" : "Copy All Codes"}</span>
                    </button>
                    <button
                      type="button"
                      className="btn-rec-action"
                      onClick={handleDownloadRecoveryCodes}
                    >
                      <DownloadIcon />
                      <span>Download .txt</span>
                    </button>
                  </div>

                  <div className="setup-action-footer center-actions" style={{ marginTop: "24px" }}>
                    <button
                      type="button"
                      className="btn-primary-green"
                      onClick={() => {
                        setViewMode("dashboard");
                        toast.success("2FA is fully enabled and backup codes are saved!");
                      }}
                    >
                      I've Saved My Codes &rarr; Complete Setup
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* DASHBOARD VIEW */}
          {viewMode === "dashboard" && (
            <div className="tfa-dashboard-content">
              {/* EMERGENCY BACKUP RECOVERY CODES CARD */}
              {/* <div className="recovery-codes-dashboard-card">
                <div className="card-header-block">
                  <div className="card-icon-title-row">
                    <div className="key-icon-badge">
                      <KeyIcon />
                    </div>
                    <div className="title-desc-wrap">
                      <div className="title-tag-row">
                        <h3>Emergency Backup Recovery Codes</h3>
                        <span className="backup-tag">Backup Codes</span>
                      </div>
                      <p className="card-desc">
                        One-time emergency codes allow you to log in to your admin account if you lose access to your authenticator app. Each code can only be used once.
                      </p>
                    </div>
                  </div>
                </div>

                {recoveryCodes.length > 0 ? (
                  <div className="recovery-codes-content">
                    <div className="recovery-notice">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <p>
                        Store these codes securely. When you use a code to log in, that specific code is invalidated.
                      </p>
                    </div>

                    <div className="recovery-codes-grid">
                      {recoveryCodes.map((code, idx) => (
                        <div key={idx} className="recovery-code-pill">
                          <span className="code-idx">{idx + 1}.</span>
                          <code>{code}</code>
                        </div>
                      ))}
                    </div>

                    <div className="recovery-dashboard-actions">
                      <div className="left-actions">
                        <button
                          type="button"
                          className={`btn-rec-action ${copiedAll ? "active" : ""}`}
                          onClick={handleCopyAllRecoveryCodes}
                        >
                          <CopyIcon />
                          <span>{copiedAll ? "Copied All!" : "Copy All Codes"}</span>
                        </button>
                        <button
                          type="button"
                          className="btn-rec-action"
                          onClick={handleDownloadRecoveryCodes}
                        >
                          <DownloadIcon />
                          <span>Download .txt</span>
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn-rec-regen"
                        onClick={() => setShowRegenModal(true)}
                        disabled={recoveryLoading}
                      >
                        <RefreshCwIcon />
                        <span>Regenerate Codes</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="recovery-empty-state">
                    <p>
                      Generate or view your emergency backup recovery codes. Keep them in a safe place in case you lose your authenticator.
                    </p>
                    <button
                      type="button"
                      className="btn-generate-codes"
                      onClick={handleFetchRecoveryCodes}
                      disabled={recoveryLoading}
                    >
                      {recoveryLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <>
                          <KeyIcon />
                          <span>View / Generate Backup Codes</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div> */}

              {/* SUPERADMIN RESET USER 2FA CARD */}
              <div className="superadmin-reset-card">
                <div className="card-header-block">
                  <div className="card-icon-title-row">
                    <div className="key-icon-badge">
                      <KeyIcon />
                    </div>
                    <div className="title-desc-wrap">
                      <div className="title-tag-row">
                        <h3>Superadmin: Reset User 2FA</h3>
                        <span className="superadmin-tag">Superadmin Tool</span>
                      </div>
                      <p className="card-desc">
                        As a Superadmin, you can reset/disable Two-Factor
                        Authentication for any admin user who is locked out of
                        their authenticator app.
                      </p>
                    </div>
                  </div>
                </div>

                <form
                  onSubmit={handleSuperadminReset}
                  className="superadmin-form"
                >
                  <div className="form-group">
                    <label htmlFor="userEmail">Target User Email Address</label>
                    <div className="input-action-row">
                      <div className="input-wrapper-with-icon">
                        <svg
                          className="input-icon"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#94a3b8"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                        <input
                          type="email"
                          id="userEmail"
                          placeholder="e.g. admin@equityedge.co.uk"
                          value={targetUserEmail}
                          onChange={(e) => setTargetUserEmail(e.target.value)}
                          required
                          className="superadmin-input"
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn-reset-user"
                        disabled={
                          superadminResetLoading || !targetUserEmail.trim()
                        }
                      >
                        {superadminResetLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <>
                            <KeyIcon />
                            <span>Reset User 2FA</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="superadmin-hint-box">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>
                      Resetting immediately clears the MFA secret for this user, enabling them to reconfigure their authenticator device upon next sign in.
                    </span>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* REGENERATE RECOVERY CODES CONFIRMATION MODAL */}
          {showRegenModal && (
            <div
              className="tfa-modal-overlay"
              onClick={() => setShowRegenModal(false)}
            >
              <div
                className="tfa-modal-dialog modal-small"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <div className="modal-title-row">
                    <KeyIcon />
                    <h3>Regenerate Recovery Codes?</h3>
                  </div>
                  <button
                    className="close-btn"
                    onClick={() => setShowRegenModal(false)}
                  >
                    &times;
                  </button>
                </div>

                <div className="modal-body">
                  <p className="confirm-turn-off-text">
                    Generating a new set of recovery codes will immediately invalidate any previous recovery codes. Make sure to copy or download your new codes.
                  </p>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn-modal-secondary"
                    onClick={() => setShowRegenModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-modal-primary"
                    onClick={handleFetchRecoveryCodes}
                    disabled={recoveryLoading}
                  >
                    {recoveryLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      "Generate New Codes"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TURN OFF 2FA CONFIRMATION MODAL */}
          {showTurnOffModal && (
            <div
              className="tfa-modal-overlay"
              onClick={() => setShowTurnOffModal(false)}
            >
              <div
                className="tfa-modal-dialog modal-small"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <div className="modal-title-row">
                    <PowerIcon />
                    <h3>Disable Two-Factor Authentication?</h3>
                  </div>
                  <button
                    className="close-btn"
                    onClick={() => setShowTurnOffModal(false)}
                  >
                    &times;
                  </button>
                </div>

                <div className="modal-body">
                  <p className="confirm-turn-off-text">
                    Turning off 2FA lowers your account security. Anyone with
                    your password will be able to log in without a secondary
                    verification step.
                  </p>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn-modal-secondary"
                    onClick={() => setShowTurnOffModal(false)}
                  >
                    Keep Protected
                  </button>
                  <button
                    className="btn-modal-danger"
                    onClick={handleConfirmTurnOff}
                    disabled={turnOffLoading}
                  >
                    {turnOffLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      "Yes, Turn Off 2FA"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuth;
